import { Button } from '@trellis/roots/button'
import { Checkbox } from '@trellis/roots/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@trellis/roots/dialog'
import { EmailInput } from '@trellis/roots/forms'
import { Input } from '@trellis/roots/input'
import { Label } from '@trellis/roots/label'
import { Textarea } from '@trellis/roots/textarea'
import { Typography } from '@trellis/roots/typography'
import { AlertCircle, Eye, Send } from 'lucide-react'
import { useState } from 'react'
import type { ReactNode } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import { EmailDivertNotice } from '@/components/email'
import { useDisclosure } from '@/hooks'
import { formatDate } from '@/lib/format'

export interface SendDocumentEmailBody {
  to?: string
  subject?: string
  message?: string
  force?: boolean
}

interface SendDocumentMutation {
  isPending: boolean
  mutate(
    body: SendDocumentEmailBody,
    options: { onSuccess: () => void; onError: (caughtError: Error) => void },
  ): void
}

interface SendDocumentDialogProps {
  documentLabel: 'Invoice' | 'Quote'
  documentNumber: string
  clientEmail: string
  sentAt: string | null
  orgName: string
  summary: ReactNode
  mutation: SendDocumentMutation
  isAlreadySentError: (caughtError: unknown) => boolean
  renderPreview: (isOpen: boolean, onOpenChange: (open: boolean) => void) => ReactNode
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SendDocumentDialog({
  documentLabel,
  documentNumber,
  clientEmail,
  sentAt,
  orgName,
  summary,
  mutation,
  isAlreadySentError,
  renderPreview,
  open,
  onOpenChange,
}: SendDocumentDialogProps) {
  const lowerLabel = documentLabel.toLowerCase()
  const fieldId = (suffix: string) => `send-${lowerLabel}-${suffix}`

  const [to, setTo] = useState(clientEmail)
  const [subject, setSubject] = useState(
    `${documentLabel} ${documentNumber} from ${orgName}`,
  )
  const [message, setMessage] = useState('')
  const [resendConfirmed, setResendConfirmed] = useState(false)
  const [localAlreadySent, setLocalAlreadySent] = useState(false)
  const preview = useDisclosure()

  const isResend = sentAt !== null || localAlreadySent
  const canSend = !isResend || resendConfirmed
  const toTrimmed = to.trim()
  const emailValid = z.email().safeParse(toTrimmed).success
  const showEmailError = toTrimmed.length > 0 && !emailValid

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      setResendConfirmed(false)
      setLocalAlreadySent(false)
    }
    onOpenChange(isOpen)
  }

  function handleSend() {
    mutation.mutate(
      {
        to: to.trim() || undefined,
        subject: subject.trim() || undefined,
        message: message.trim() || undefined,
        force: resendConfirmed ? true : undefined,
      },
      {
        onSuccess: () => {
          toast.success(`${documentLabel} sent to ${to.trim()}`)
          handleOpenChange(false)
        },
        onError: (caughtError) => {
          if (isAlreadySentError(caughtError)) {
            setLocalAlreadySent(true)
            setResendConfirmed(false)
            return
          }
          const msg =
            caughtError instanceof Error
              ? caughtError.message
              : `Failed to send ${lowerLabel}`
          toast.error(`Failed to send ${lowerLabel}`, { description: msg })
        },
      },
    )
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader className="flex-row items-center justify-between space-y-0">
            <DialogTitle>Send {documentLabel}</DialogTitle>
            <Button variant="ghost" size="sm" onClick={() => preview.open(true)}>
              <Eye className="mr-2 size-4" />
              Preview
            </Button>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor={fieldId('to')}>To</Label>
              <EmailInput
                id={fieldId('to')}
                value={to}
                onChange={(changeEvent) => setTo(changeEvent.target.value)}
                placeholder="client@example.com"
                aria-invalid={showEmailError}
              />
              {showEmailError && (
                <Typography variant="body-sm" color="destructive">
                  Enter a valid email address.
                </Typography>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor={fieldId('subject')}>Subject</Label>
              <Input
                id={fieldId('subject')}
                value={subject}
                onChange={(changeEvent) => setSubject(changeEvent.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor={fieldId('message')}>Message (optional)</Label>
              <Textarea
                id={fieldId('message')}
                value={message}
                onChange={(changeEvent) => setMessage(changeEvent.target.value)}
                rows={3}
                placeholder={`Please find attached ${lowerLabel} ${documentNumber}…`}
              />
            </div>

            <div className="bg-muted/40 rounded-lg border px-4 py-3">
              <Typography variant="body-sm" color="muted">
                {summary}
              </Typography>
            </div>

            <EmailDivertNotice />

            {isResend && (
              <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <div className="flex gap-2">
                  <AlertCircle className="mt-0.5 size-4 shrink-0 text-amber-600" />
                  <Typography variant="body-sm" className="text-amber-800">
                    Already sent {sentAt ? formatDate(sentAt) : 'just now'}. Sending again
                    will deliver a duplicate email to the client.
                  </Typography>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={fieldId('resend-confirm')}
                    checked={resendConfirmed}
                    onCheckedChange={(checked) => setResendConfirmed(checked === true)}
                  />
                  <Label
                    htmlFor={fieldId('resend-confirm')}
                    className="cursor-pointer text-sm text-amber-800"
                  >
                    Send again (client will receive a duplicate)
                  </Label>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={!canSend || mutation.isPending || !emailValid}
            >
              <Send className="mr-2 size-4" />
              {mutation.isPending ? 'Sending…' : `Send ${documentLabel}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {renderPreview(preview.isOpen, preview.onOpenChange)}
    </>
  )
}
